import { Clock, Coins, MapPin, MessageSquare, Power, RefreshCw, Send, SlidersHorizontal, Star, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { parseSystemFlags } from "./types";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface HrSettingsTabProps {
  performanceAdmin: any;
  performanceDraft: {
    lineUserId: string;
    displayName: string;
    reviewPeriod: string;
    qualityScore: number;
    productivityScore: number;
    teamworkScore: number;
    attendanceScore: number;
    managerFeedback: string;
  };
  setPerformanceDraft: React.Dispatch<
    React.SetStateAction<{
      lineUserId: string;
      displayName: string;
      reviewPeriod: string;
      qualityScore: number;
      productivityScore: number;
      teamworkScore: number;
      attendanceScore: number;
      managerFeedback: string;
    }>
  >;
  savePerformance: any;
  payrollSettings: any;
  settingsDraft: {
    payrollPeriodStart: string;
    payrollPeriodEnd: string;
    standardStart: string;
    standardEnd: string;
    latenessGraceMinutes: number;
    payableWorkdays: number;
    standardWorkHours: number;
    otMultiplier: number;
  };
  setSettingsDraft: React.Dispatch<
    React.SetStateAction<{
      payrollPeriodStart: string;
      payrollPeriodEnd: string;
      standardStart: string;
      standardEnd: string;
      latenessGraceMinutes: number;
      payableWorkdays: number;
      standardWorkHours: number;
      otMultiplier: number;
    }>
  >;
  saveSettings: () => void;
  savePayrollSettings: any;
  salaryDraft: {
    displayName: string;
    monthlySalary: string;
    dailyRate: string;
    hourlyRate: string;
    effectiveFrom: string;
  };
  setSalaryDraft: React.Dispatch<
    React.SetStateAction<{
      displayName: string;
      monthlySalary: string;
      dailyRate: string;
      hourlyRate: string;
      effectiveFrom: string;
    }>
  >;
  saveSalary: () => void;
  saveSalaryProfile: any;
  payrollProfiles: any;
  idToken: string | null;
  systemConfigs?: Record<string, string>;
  updateSystemConfig?: any;
}

export function HrSettingsTab({
  performanceAdmin,
  performanceDraft,
  setPerformanceDraft,
  savePerformance,
  payrollSettings,
  settingsDraft,
  setSettingsDraft,
  saveSettings,
  savePayrollSettings,
  salaryDraft,
  setSalaryDraft,
  saveSalary,
  saveSalaryProfile,
  payrollProfiles,
  idToken,
  systemConfigs,
  updateSystemConfig,
}: HrSettingsTabProps) {
  const flags = parseSystemFlags(systemConfigs);

  const sendGroupSummary = trpc.attendance.sendDailyGroupSummary.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success("📢 ส่งสรุปยอดเข้ากลุ่ม LINE เรียบร้อยแล้ว!");
      } else {
        toast.warning(data.error || "ระบบส่งคำขอแล้ว แต่ LINE API แจ้งเตือนข้อผิดพลาด");
      }
    },
    onError: (err) => {
      toast.error(err.message || "เกิดข้อผิดพลาดในการส่งเข้ากลุ่ม LINE");
    },
  });

  const handleTestSendGroupSummary = () => {
    if (!idToken) {
      toast.error("กรุณาเข้าสู่ระบบก่อนดำเนินการ");
      return;
    }
    const groupId = systemConfigs?.lineSummaryGroupId?.trim();
    if (!groupId) {
      toast.error("กรุณาระบุ LINE Group ID ก่อนกดทดสอบส่ง");
      return;
    }
    sendGroupSummary.mutate({ idToken, groupId });
  };

  const toggleFeature = (key: string, currentValue: boolean) => {
    if (!idToken || !updateSystemConfig) return;
    updateSystemConfig.mutate({
      idToken,
      configKey: key,
      configValue: currentValue ? "false" : "true",
    });
  };
  return (
    <div className="space-y-5">
      {/* Salary Advance Rules Setting Card */}
      <Card className="glass-panel rounded-3xl border-white/80 shadow-md">
        <CardHeader className="p-5 sm:p-6 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-amber-500 to-orange-500 text-white shadow-sm">
              <Coins size={18} />
            </div>
            <div>
              <CardTitle className="text-base font-black text-slate-900">
                ตั้งค่าเงื่อนไขการเบิกเงินล่วงหน้า (Salary Advance Rules)
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">
                กำหนดความถี่และเงื่อนไขวันทำงานในการขอเบิกเงินล่วงหน้า (ตั้งค่าได้จริงและมีผลทันที)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-6 pt-0 space-y-4">
          <div className="rounded-2xl bg-amber-50/80 border border-amber-200/80 p-4">
            <p className="text-xs font-black text-amber-900">
              📌 กฎปัจจุบัน: ขอเบิกได้ 1 ครั้ง ทุกๆ {systemConfigs?.advanceCooldownDays || "2"} วัน | ทำงานครบ {systemConfigs?.advanceWorkDaysPerCut || "3"} วัน ตัดสิทธิ์ให้เบิก {systemConfigs?.advanceAllowedCuts || "2"} วัน
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1.5">
                ระยะห่างการเบิก (วัน/ครั้ง)
              </label>
              <input
                type="number"
                min="1"
                max="30"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 focus:border-amber-500 outline-none"
                defaultValue={systemConfigs?.advanceCooldownDays || "2"}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val && idToken && updateSystemConfig) {
                    updateSystemConfig.mutate({ idToken, configKey: "advanceCooldownDays", configValue: val });
                  }
                }}
              />
              <p className="text-[11px] text-slate-500 mt-1">เช่น 2 วัน เบิกได้ที่ 1 ครั้ง</p>
            </div>
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1.5">
                วันทำงานสะสมต่อรอบตัด (วัน)
              </label>
              <input
                type="number"
                min="1"
                max="30"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 focus:border-amber-500 outline-none"
                defaultValue={systemConfigs?.advanceWorkDaysPerCut || "3"}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val && idToken && updateSystemConfig) {
                    updateSystemConfig.mutate({ idToken, configKey: "advanceWorkDaysPerCut", configValue: val });
                  }
                }}
              />
              <p className="text-[11px] text-slate-500 mt-1">เช่น ทำ 3 ครั้ง (3 วัน)</p>
            </div>
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1.5">
                จำนวนวันที่ตัดให้เบิก (วัน)
              </label>
              <input
                type="number"
                min="1"
                max="30"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 focus:border-amber-500 outline-none"
                defaultValue={systemConfigs?.advanceAllowedCuts || "2"}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val && idToken && updateSystemConfig) {
                    updateSystemConfig.mutate({ idToken, configKey: "advanceAllowedCuts", configValue: val });
                  }
                }}
              />
              <p className="text-[11px] text-slate-500 mt-1">เช่น ตัดสิทธิ์ให้เบิก 2 ครั้ง (2 วัน)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LINE Group Summary Settings Card */}
      <Card className="glass-panel rounded-3xl border-white/80 shadow-md">
        <CardHeader className="p-5 sm:p-6 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-sm">
              <MessageSquare size={18} />
            </div>
            <div>
              <CardTitle className="text-base font-black text-slate-900">
                แจ้งเตือนสรุปยอดเข้ากลุ่ม LINE (LINE Group Summary)
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">
                ส่งรายงานสรุปยอดการลงเวลาและเบิกเงินประจำวันเข้า LINE Group ของทีม/ผู้บริหาร
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-6 pt-0 space-y-4">
          <div className="rounded-2xl bg-emerald-50/70 border border-emerald-200/80 p-4 space-y-2">
            <p className="text-xs font-black text-emerald-950 flex items-center gap-1.5">
              <span>💡</span> วิธีเชื่อมต่อกลุ่ม LINE เพื่อรับสรุปยอด
            </p>
            <ol className="text-xs font-semibold text-emerald-900 space-y-1 list-decimal list-inside">
              <li>เชิญ LINE Bot (บัญชีทางการของบริษัท) เข้ากลุ่ม LINE ที่ต้องการ</li>
              <li>พิมพ์คำว่า <code className="bg-emerald-200/80 px-1.5 py-0.5 rounded text-emerald-950 font-bold">ไอดีกลุ่ม</code> หรือ <code className="bg-emerald-200/80 px-1.5 py-0.5 rounded text-emerald-950 font-bold">groupid</code> ในกลุ่ม บอทจะตอบรหัสกลุ่มกลับมาให้อัตโนมัติ</li>
              <li>นำรหัส Group ID (ขึ้นต้นด้วยตัว C) มาใส่ในช่องด้านล่างนี้</li>
            </ol>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1.5">
                LINE Group ID (รหัสกลุ่มรับแจ้งเตือน)
              </label>
              <input
                type="text"
                placeholder="เช่น C1234567890abcdef1234567890abcdef"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 outline-none font-mono"
                defaultValue={systemConfigs?.lineSummaryGroupId || ""}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (idToken && updateSystemConfig) {
                    updateSystemConfig.mutate({ idToken, configKey: "lineSummaryGroupId", configValue: val });
                  }
                }}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                รหัสกลุ่ม LINE จะขึ้นต้นด้วยตัว C (33 ตัวอักษร)
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestSendGroupSummary}
                disabled={sendGroupSummary.isPending}
                className="rounded-xl font-bold text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <Send size={14} className="mr-1.5" />
                {sendGroupSummary.isPending ? "กำลังส่งสรุปเข้ากลุ่ม..." : "📢 ทดสอบส่งสรุปยอดเข้ากลุ่ม"}
              </Button>

              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-slate-700">ส่งอัตโนมัติประจำวัน:</span>
                <button
                  onClick={() => toggleFeature("autoSendDailySummary", systemConfigs?.autoSendDailySummary === "true")}
                  className={`px-3 py-1 rounded-xl text-xs font-black transition-colors ${
                    systemConfigs?.autoSendDailySummary === "true"
                      ? "bg-emerald-600 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {systemConfigs?.autoSendDailySummary === "true" ? "เปิดอยู่" : "ปิดอยู่"}
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Feature Toggle Control Card */}
      <Card className="glass-panel rounded-3xl border-white/80 shadow-md">
        <CardHeader className="p-5 sm:p-6 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-teal-600 to-cyan-500 text-white shadow-sm">
              <Power size={18} />
            </div>
            <div>
              <CardTitle className="text-base font-black text-slate-900">
                เปิด / ปิด ฟีเจอร์ระบบ (Dynamic Feature Toggles)
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">
                เปิดหรือปิดการทำงานของโมดูลระบบได้แบบไดนามิกโดยไม่ต้องแก้ไขโค้ด
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-6 pt-0 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div
              className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                flags.enableSalaryAdvance
                  ? "bg-amber-50/80 border-amber-300"
                  : "bg-slate-50 border-slate-300 opacity-85"
              }`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className={`p-2 rounded-xl shrink-0 ${flags.enableSalaryAdvance ? "bg-amber-500 text-white" : "bg-slate-300 text-slate-600"}`}>
                  <Coins size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-slate-900">อนุมัติเบิกเงินล่วงหน้า</p>
                  <p className="text-[11px] font-semibold text-slate-600">ควบคุมทั้งการยื่นคำขอและการอนุมัติ</p>
                </div>
              </div>
              <button
                onClick={() => toggleFeature("enableSalaryAdvance", flags.enableSalaryAdvance)}
                disabled={updateSystemConfig?.isPending}
                className="shrink-0 transition-transform active:scale-95"
                aria-pressed={flags.enableSalaryAdvance}
              >
                <span className={`flex items-center px-3 py-1.5 rounded-xl text-white text-xs font-black ${flags.enableSalaryAdvance ? "bg-amber-500" : "bg-slate-400"}`}>
                  {flags.enableSalaryAdvance ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                </span>
              </button>
            </div>

            {/* Toggle 2: Payroll & Overtime */}
            <div
              className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                flags.enableOvertime
                  ? "bg-teal-50/70 border-teal-200"
                  : "bg-slate-50 border-slate-200 opacity-85"
              }`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className={`p-2 rounded-xl shrink-0 ${
                    flags.enableOvertime
                      ? "bg-teal-600 text-white"
                      : "bg-slate-300 text-slate-600"
                  }`}
                >
                  <Clock size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-slate-900 truncate">
                    ระบบคำนวณเงินเดือน & OT
                  </p>
                  <p className="text-[11px] font-semibold text-slate-500 line-clamp-1">
                    แสดงการคำนวณ OT และประมาณการค่าจ้าง
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleFeature("enableOvertime", flags.enableOvertime)}
                disabled={updateSystemConfig?.isPending}
                className="shrink-0 transition-transform active:scale-95"
              >
                {flags.enableOvertime ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-600 text-white text-xs font-black shadow-xs">
                    เปิดใช้งาน
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-300 text-slate-700 text-xs font-black">
                    ปิดใช้งาน
                  </span>
                )}
              </button>
            </div>

            {/* Toggle 3: Performance Review */}
            <div
              className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                flags.enablePerformanceReview
                  ? "bg-teal-50/70 border-teal-200"
                  : "bg-slate-50 border-slate-200 opacity-85"
              }`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className={`p-2 rounded-xl shrink-0 ${
                    flags.enablePerformanceReview
                      ? "bg-teal-600 text-white"
                      : "bg-slate-300 text-slate-600"
                  }`}
                >
                  <Star size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-slate-900 truncate">
                    ระบบประเมินผลการทำงาน
                  </p>
                  <p className="text-[11px] font-semibold text-slate-500 line-clamp-1">
                    ประเมินคะแนนและส่งคอมเมนต์ผลงาน
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  toggleFeature("enablePerformanceReview", flags.enablePerformanceReview)
                }
                disabled={updateSystemConfig?.isPending}
                className="shrink-0 transition-transform active:scale-95"
              >
                {flags.enablePerformanceReview ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-600 text-white text-xs font-black shadow-xs">
                    เปิดใช้งาน
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-300 text-slate-700 text-xs font-black">
                    ปิดใช้งาน
                  </span>
                )}
              </button>
            </div>

            {/* Toggle 4: GPS Requirement */}
            <div
              className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                flags.enableGPSLocation
                  ? "bg-teal-50/70 border-teal-200"
                  : "bg-slate-50 border-slate-200 opacity-85"
              }`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className={`p-2 rounded-xl shrink-0 ${
                    flags.enableGPSLocation
                      ? "bg-teal-600 text-white"
                      : "bg-slate-300 text-slate-600"
                  }`}
                >
                  <MapPin size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-slate-900 truncate">
                    เก็บบันทึกพิกัด GPS ตอนลงเวลา
                  </p>
                  <p className="text-[11px] font-semibold text-slate-500 line-clamp-1">
                    บันทึกละติจูด/ลองจิจูดขณะเช็คอิน-เช็คเอาท์
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleFeature("enableGPSLocation", flags.enableGPSLocation)}
                disabled={updateSystemConfig?.isPending}
                className="shrink-0 transition-transform active:scale-95"
              >
                {flags.enableGPSLocation ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-600 text-white text-xs font-black shadow-xs">
                    เปิดใช้งาน
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-300 text-slate-700 text-xs font-black">
                    ปิดใช้งาน
                  </span>
                )}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
      {performanceAdmin.data && (
        <Card className="glass-panel mb-5 rounded-3xl">
          <CardHeader>
            <CardTitle>ประเมินผลการปฏิบัติงาน</CardTitle>
            <CardDescription>
              กำหนดคะแนน 1–5 และเผยแพร่ให้พนักงานดูใน “สถิติของฉัน”
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <select
                className="glass-input h-11 rounded-xl px-3 text-sm font-bold"
                value={performanceDraft.lineUserId}
                onChange={(e) => {
                  const employee = performanceAdmin.data.employees.find(
                    (x: any) => x.lineUserId === e.target.value
                  );
                  setPerformanceDraft({
                    ...performanceDraft,
                    lineUserId: e.target.value,
                    displayName: employee?.displayName ?? "",
                  });
                }}
              >
                <option value="">เลือกพนักงาน</option>
                {performanceAdmin.data.employees.map((employee: any) => (
                  <option key={employee.lineUserId} value={employee.lineUserId}>
                    {employee.displayName}
                  </option>
                ))}
              </select>
              <input
                className="glass-input h-11 rounded-xl px-3"
                value={performanceDraft.reviewPeriod}
                onChange={(e) =>
                  setPerformanceDraft({
                    ...performanceDraft,
                    reviewPeriod: e.target.value,
                  })
                }
                placeholder="2026-H2"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(
                [
                  ["คุณภาพงาน", "qualityScore"],
                  ["ผลงาน", "productivityScore"],
                  ["ทีมเวิร์ก", "teamworkScore"],
                  ["วินัยเวลา", "attendanceScore"],
                ] as const
              ).map(([label, key]) => (
                <label key={key} className="text-xs font-bold text-slate-600">
                  {label}
                  <select
                    className="glass-input mt-1 h-10 w-full rounded-xl px-2"
                    value={performanceDraft[key]}
                    onChange={(e) =>
                      setPerformanceDraft({
                        ...performanceDraft,
                        [key]: Number(e.target.value),
                      })
                    }
                  >
                    {[1, 2, 3, 4, 5].map((score) => (
                      <option key={score}>{score}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <textarea
              className="glass-input min-h-24 w-full rounded-xl p-3"
              placeholder="ข้อเสนอแนะจากผู้ประเมิน"
              value={performanceDraft.managerFeedback}
              onChange={(e) =>
                setPerformanceDraft({
                  ...performanceDraft,
                  managerFeedback: e.target.value,
                })
              }
            />
            <Button
              className="btn-primary-teal w-full"
              disabled={
                !performanceDraft.lineUserId ||
                !performanceDraft.managerFeedback ||
                savePerformance.isPending
              }
              onClick={() =>
                idToken &&
                savePerformance.mutate({
                  idToken,
                  ...performanceDraft,
                })
              }
            >
              บันทึกและเผยแพร่ผลประเมิน
            </Button>
          </CardContent>
        </Card>
      )}

      {payrollSettings.data && (
        <Card className="glass-panel rounded-3xl border-white/80 shadow-md">
          <CardHeader className="p-5 sm:p-6 pb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                <SlidersHorizontal size={18} />
              </div>
              <div>
                <CardTitle className="text-base font-black text-slate-900">
                  ตั้งค่ากติกาเงินเดือน & OT
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  ปรับเปลี่ยนการคำนวณในระบบโดยไม่กระทบข้อมูล Google Sheet
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-5 sm:p-6 pt-0 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  เริ่มรอบ (วันที่)
                </label>
                <input
                  type="text"
                  value={settingsDraft.payrollPeriodStart}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      payrollPeriodStart: e.target.value,
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                  placeholder="01"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  สิ้นสุดรอบ
                </label>
                <input
                  type="text"
                  value={settingsDraft.payrollPeriodEnd}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      payrollPeriodEnd: e.target.value,
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                  placeholder="last"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  เวลาเริ่มงาน
                </label>
                <input
                  type="time"
                  value={settingsDraft.standardStart}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      standardStart: e.target.value,
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  เวลาเลิกงาน
                </label>
                <input
                  type="time"
                  value={settingsDraft.standardEnd}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      standardEnd: e.target.value,
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  ผ่อนผันสาย (นาที)
                </label>
                <input
                  type="number"
                  value={settingsDraft.latenessGraceMinutes}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      latenessGraceMinutes: Number(e.target.value),
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  วันทำงาน/เดือน
                </label>
                <input
                  type="number"
                  value={settingsDraft.payableWorkdays}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      payableWorkdays: Number(e.target.value),
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  ชั่วโมงปกติ/วัน
                </label>
                <input
                  type="number"
                  value={settingsDraft.standardWorkHours}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      standardWorkHours: Number(e.target.value),
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                />
              </div>
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  อัตรา OT (%)
                </label>
                <input
                  type="number"
                  value={settingsDraft.otMultiplier}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      otMultiplier: Number(e.target.value),
                    })
                  }
                  className="glass-input mt-1 h-10 w-full rounded-xl px-3 text-xs font-bold"
                />
              </div>
            </div>

            <Button
              className="btn-primary-teal h-11 w-full rounded-xl font-bold text-xs"
              onClick={saveSettings}
              disabled={savePayrollSettings.isPending}
            >
              {savePayrollSettings.isPending && (
                <RefreshCw className="mr-2 animate-spin" size={14} />
              )}
              บันทึกการตั้งค่าเงินเดือน & OT
            </Button>

            <div className="pt-4 border-t border-slate-100">
              <p className="text-xs font-black text-slate-900 mb-2">
                กำหนดอัตราค่าจ้างรายบุคคล
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                <input
                  placeholder="ชื่อ-นามสกุลพนักงาน"
                  value={salaryDraft.displayName}
                  onChange={(e) =>
                    setSalaryDraft({ ...salaryDraft, displayName: e.target.value })
                  }
                  className="glass-input h-10 rounded-xl px-3 text-xs font-semibold"
                />
                <input
                  placeholder="เงินเดือนประจำ (บาท)"
                  type="number"
                  value={salaryDraft.monthlySalary}
                  onChange={(e) =>
                    setSalaryDraft({ ...salaryDraft, monthlySalary: e.target.value })
                  }
                  className="glass-input h-10 rounded-xl px-3 text-xs font-semibold"
                />
                <input
                  placeholder="ค่าจ้างรายวัน (ถ้ามี)"
                  type="number"
                  value={salaryDraft.dailyRate}
                  onChange={(e) =>
                    setSalaryDraft({ ...salaryDraft, dailyRate: e.target.value })
                  }
                  className="glass-input h-10 rounded-xl px-3 text-xs font-semibold"
                />
                <input
                  placeholder="ค่าจ้างราย ชม. (ถ้ามี)"
                  type="number"
                  value={salaryDraft.hourlyRate}
                  onChange={(e) =>
                    setSalaryDraft({ ...salaryDraft, hourlyRate: e.target.value })
                  }
                  className="glass-input h-10 rounded-xl px-3 text-xs font-semibold"
                />
                <input
                  placeholder="มีผลตั้งแต่ (YYYY-MM-DD)"
                  type="date"
                  value={salaryDraft.effectiveFrom}
                  onChange={(e) =>
                    setSalaryDraft({ ...salaryDraft, effectiveFrom: e.target.value })
                  }
                  className="glass-input h-10 rounded-xl px-3 text-xs font-semibold"
                />
                <Button
                  variant="outline"
                  className="h-10 rounded-xl font-bold text-xs"
                  onClick={saveSalary}
                  disabled={!salaryDraft.displayName || saveSalaryProfile.isPending}
                >
                  บันทึกอัตราค่าจ้าง
                </Button>
              </div>

              <div className="mt-3 space-y-1 max-h-32 overflow-y-auto pr-1">
                {payrollProfiles.data?.map((profile: any) => (
                  <div
                    key={profile.displayName}
                    className="flex items-center justify-between text-xs py-1 px-2 rounded-lg bg-slate-50 font-semibold text-slate-600"
                  >
                    <span>{profile.displayName}</span>
                    <span className="font-numeric text-teal-700">
                      {profile.monthlySalary
                        ? `฿${profile.monthlySalary.toLocaleString()}/เดือน`
                        : profile.dailyRate
                        ? `฿${profile.dailyRate.toLocaleString()}/วัน`
                        : "ยังไม่ระบุอัตรา"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
